import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faCalendarCheck,
  faArrowUpRightFromSquare,
  faCopy,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";
import { personalInfo } from "../data/portfolioData";

export default function ContactSection() {
  const [copied, setCopied] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(personalInfo.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="contact" className="cyber-section pb-5">
      <div className="container">
        {/* 1. Pre-Contact Resume CTA Banner */}
        <div
          className="cyber-card p-4 p-md-5 mb-5 text-center position-relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(14, 17, 22, 0.95) 0%, rgba(21, 25, 34, 0.95) 100%)",
            border: "1px solid rgba(56, 189, 248, 0.3)",
            boxShadow: "0 20px 40px -15px rgba(0, 0, 0, 0.8)",
          }}
        >
          <div className="section-label mx-auto mb-3">COLLABORATION &amp; ROLES</div>
          <h2
            style={{
              fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "var(--text-primary)",
              marginBottom: "16px",
            }}
          >
            Looking for someone who works hands-on with security?
          </h2>

          <p
            style={{
              fontSize: "1.05rem",
              color: "var(--text-secondary)",
              maxWidth: "600px",
              margin: "0 auto 32px auto",
              lineHeight: 1.6,
            }}
          >
            Explore my experience, projects and security work — ready to contribute across SIEM operations,
            vulnerability assessment, and infrastructure hardening.
          </p>

          <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
            <a href="#direct-contact" className="btn-cyber-primary">
              Contact Me
            </a>

            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cyber-secondary"
            >
              Connect on LinkedIn
            </a>
          </div>
        </div>

        {/* 2. Main Contact Card */}
        <div id="direct-contact" className="pt-3">
          <div className="row g-4 align-items-stretch">
            {/* Left: Direct Contact Information */}
            <div className="col-lg-6">
              <div
                className="cyber-card p-4 p-md-5 h-100 d-flex flex-column justify-content-between"
                style={{ background: "var(--bg-surface)" }}
              >
                <div>
                  <div className="section-label mb-3">GET IN TOUCH</div>
                  <h2 className="section-title mb-3" style={{ fontSize: "2rem" }}>
                    Let's Talk Security.
                  </h2>
                  <p style={{ color: "var(--text-secondary)", fontSize: "1rem", lineHeight: 1.7, marginBottom: "32px" }}>
                    Open to technical collaboration, security projects, and meaningful conversations around security operations and threat defense.
                  </p>

                  <div className="d-flex flex-column gap-3 mb-4">
                    {/* Email item */}
                    <div
                      className="p-3 rounded-2 d-flex align-items-center justify-content-between"
                      style={{
                        background: "var(--bg-surface-elevated)",
                        border: "1px solid var(--border-subtle)",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: "8px",
                            background: "rgba(56, 189, 248, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--accent)",
                          }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </div>
                        <div>
                          <div className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                            DIRECT EMAIL
                          </div>
                          <a
                            href={`mailto:${personalInfo.email}`}
                            style={{ color: "var(--text-primary)", textDecoration: "none", fontWeight: 600, fontSize: "0.92rem" }}
                          >
                            {personalInfo.email}
                          </a>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={copyEmail}
                        className="btn btn-sm btn-cyber-ghost font-mono"
                        style={{ fontSize: "0.75rem" }}
                        title="Copy email address"
                      >
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-1" />
                        {copied ? "Copied" : "Copy"}
                      </button>
                    </div>

                    {/* LinkedIn */}
                    <a
                      href={personalInfo.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-2 d-flex align-items-center justify-content-between text-decoration-none"
                      style={{
                        background: "var(--bg-surface-elevated)",
                        border: "1px solid var(--border-subtle)",
                        transition: "var(--transition)",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: "8px",
                            background: "rgba(10, 102, 194, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#38BDF8",
                          }}
                        >
                          <FontAwesomeIcon icon={faLinkedin} style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <div className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                            PROFESSIONAL NETWORK
                          </div>
                          <div style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "0.92rem" }}>
                            LinkedIn Profile
                          </div>
                        </div>
                      </div>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ color: "var(--text-muted)", fontSize: "0.8rem" }} />
                    </a>

                    {/* GitHub */}
                    <a
                      href={personalInfo.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-2 d-flex align-items-center justify-content-between text-decoration-none"
                      style={{
                        background: "var(--bg-surface-elevated)",
                        border: "1px solid var(--border-subtle)",
                        transition: "var(--transition)",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: "8px",
                            background: "rgba(255, 255, 255, 0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-primary)",
                          }}
                        >
                          <FontAwesomeIcon icon={faGithub} style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <div className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                            OPEN SOURCE &amp; CODE
                          </div>
                          <div style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "0.92rem" }}>
                            github.com/Dibyataru-Chakraborty
                          </div>
                        </div>
                      </div>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ color: "var(--text-muted)", fontSize: "0.8rem" }} />
                    </a>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                  <span className="status-dot" />
                  <span className="font-mono text-muted" style={{ fontSize: "0.78rem" }}>
                    Typically responds within 24 hours
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Meeting Calendar & Direct Schedule Booking */}
            <div className="col-lg-6">
              <div
                className="cyber-card p-4 p-md-5 h-100 d-flex flex-column justify-content-between"
                style={{ background: "var(--bg-surface)" }}
              >
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="section-label mb-0">SCHEDULE A CALL</span>
                    <FontAwesomeIcon icon={faCalendarCheck} style={{ color: "var(--accent)" }} />
                  </div>

                  <h3 style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: "12px", color: "var(--text-primary)" }}>
                    Schedule a 1-on-1 Discussion
                  </h3>

                  <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", lineHeight: 1.6, marginBottom: "20px" }}>
                    Prefer a direct voice or video meeting? Pick a convenient time on my calendar to discuss potential roles, security audits, or technical questions.
                  </p>

                  {!showCalendar ? (
                    <div
                      className="p-4 rounded-2 text-center my-3"
                      style={{
                        background: "var(--bg-surface-elevated)",
                        border: "1px dashed var(--border-accent)",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCalendarCheck}
                        style={{ fontSize: "2.2rem", color: "var(--accent)", marginBottom: "16px" }}
                      />
                      <h4 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "8px" }}>
                        Instant Meeting Scheduler
                      </h4>
                      <p className="text-muted mb-4" style={{ fontSize: "0.85rem" }}>
                        Powered by Zcal • Select your timezone &amp; available time slot
                      </p>
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowCalendar(true)}
                          className="btn-cyber-primary"
                        >
                          Load Interactive Calendar
                        </button>
                        <a
                          href={personalInfo.zcal}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-cyber-secondary"
                        >
                          Open Zcal <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: "0.75rem" }} />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="rounded-2 overflow-hidden position-relative"
                      style={{
                        border: "1px solid var(--border-subtle)",
                        background: "#fff",
                        height: "480px",
                      }}
                    >
                      <iframe
                        src={personalInfo.zcalEmbed}
                        title="Schedule Discussion with Dibyataru Chakraborty"
                        style={{ width: "100%", height: "100%", border: "none" }}
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                  )}
                </div>

                <div className="font-mono text-muted pt-3" style={{ fontSize: "0.75rem", borderTop: "1px solid var(--border-subtle)" }}>
                  Timezone: Asia/Kolkata (IST • UTC+5:30)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

